import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { usePaddleCheckout } from "@/hooks/use-paddle-checkout";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Pricing — legaldecoder" },
      {
        name: "description",
        content:
          "One-time payment. No subscriptions. Credits valid for 12 months. Analyze contracts from $1.99 each.",
      },
      { property: "og:title", content: "legaldecoder Pricing" },
      {
        property: "og:description",
        content: "Simple, one-time pricing for renters, freelancers, and teams.",
      },
      { property: "og:url", content: "https://legaldecoder.io/pricing" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "legaldecoder Contract Analysis Credits",
          description:
            "AI-powered contract analysis for renters, freelancers, and small businesses. One-time purchase, credits valid for 12 months.",
          brand: { "@type": "Brand", name: "legaldecoder" },
          offers: [
            {
              "@type": "Offer",
              name: "Starter",
              price: "29",
              priceCurrency: "USD",
              url: "https://legaldecoder.io/pricing",
              availability: "https://schema.org/InStock",
            },
            {
              "@type": "Offer",
              name: "Pro",
              price: "59",
              priceCurrency: "USD",
              url: "https://legaldecoder.io/pricing",
              availability: "https://schema.org/InStock",
            },
            {
              "@type": "Offer",
              name: "Team",
              price: "149",
              priceCurrency: "USD",
              url: "https://legaldecoder.io/pricing",
              availability: "https://schema.org/InStock",
            },
          ],
        }),
      },
    ],
  }),
  component: Pricing,
});

type Plan = {
  id: string;
  name: string;
  badge?: { label: string; bg: string; color: string };
  scenario: string;
  price: string;
  sub?: string;
  originalPrice?: string;
  perDoc: string;
  features: string[];
  cta: string;
  ctaVariant: "ghost" | "primary";
  priceId: string | null;
  featured?: boolean;
};

const plans: Plan[] = [
  {
    id: "free",
    name: "Free Trial",
    badge: { label: "Free Trial", bg: "#DCFCE7", color: "#166534" },
    scenario: "Try before you buy",
    price: "$0",
    perDoc: "Free",
    features: [
      "2 document analyses",
      "All premium features",
      "5 AI questions per document",
      "12 months validity",
      "No credit card required",
    ],
    cta: "Start Free",
    ctaVariant: "ghost",
    priceId: null,
  },
  {
    id: "starter",
    name: "Starter",
    scenario: "For occasional use — review a lease, check a service contract",
    price: "$29",
    sub: "one-time",
    perDoc: "$2.90 per document",
    features: [
      "10 document analyses",
      "5 AI questions per document",
      "All premium features",
      "12 months validity",
    ],
    cta: "Get Started",
    ctaVariant: "primary",
    priceId: "credits_starter",
  },
  {
    id: "pro",
    name: "Professional",
    badge: { label: "MOST POPULAR", bg: "#1B3A6B", color: "#FFFFFF" },
    scenario: "For regular users — freelancers, entrepreneurs, frequent signers",
    price: "$59",
    sub: "one-time · Save 19%",
    originalPrice: "$72.50",
    perDoc: "$2.36 per document",
    features: [
      "25 document analyses",
      "5 AI questions per document",
      "All premium features",
      "Priority email support",
      "12 months validity",
    ],
    cta: "Get Started",
    ctaVariant: "primary",
    priceId: "credits_pro",
    featured: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    scenario: "For teams & high volume — agencies, law firms, HR departments",
    price: "$199",
    sub: "one-time · Save 31%",
    originalPrice: "$290",
    perDoc: "$1.99 per document",
    features: [
      "100 document analyses",
      "5 AI questions per document",
      "All premium features",
      "Priority support",
      "12 months validity",
    ],
    cta: "Contact Sales",
    ctaVariant: "ghost",
    priceId: null,
  },
];

const questionPacks = [
  {
    id: "q10",
    label: "10 Questions",
    price: "$9",
    per: "$0.90 each",
    save: null,
    priceId: "questions_10",
  },
  {
    id: "q25",
    label: "25 Questions",
    price: "$19",
    per: "$0.76 each",
    save: "Save 15%",
    priceId: "questions_25",
  },
  {
    id: "q50",
    label: "50 Questions",
    price: "$35",
    per: "$0.70 each",
    save: "Save 22%",
    priceId: "questions_50",
  },
];

const faqs = [
  {
    q: "What does 1 credit include?",
    a: "1 credit = 1 full document analysis. You get the verdict, key terms, red flags, suggested questions, and 5 AI follow-up questions about that document — all in plain English.",
  },
  {
    q: "Is this a subscription?",
    a: "No. Every pack is a one-time payment. No recurring charges, no auto-renew, no surprises. Your credits stay valid for 12 months from purchase.",
  },
  {
    q: "Can I get a refund?",
    a: "Yes — we offer a 7-day satisfaction guarantee. If legaldecoder doesn't help you understand your contract, email us within 7 days of purchase and we'll refund any unused credits, no questions asked.",
  },
  {
    q: "What jurisdictions do you cover?",
    a: "legaldecoder works on contracts from any English-speaking jurisdiction (US, UK, Canada, Australia, Ireland, NZ, and more). Our AI flags risks against widely accepted contract norms — but it is not a substitute for jurisdiction-specific legal advice.",
  },
  {
    q: "Can I share credits with my team?",
    a: "Starter and Professional packs are tied to a single account. The Enterprise pack supports team sharing — contact sales and we'll set up shared access for your organization.",
  },
  {
    q: "What if I need help?",
    a: "Email support@legaldecoder.io anytime. Professional and Enterprise customers get priority response (under 24 hours on business days).",
  },
];

function Pricing() {
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const { openCheckout, loading } = usePaddleCheckout();
  const navigate = useNavigate();
  const [activePriceId, setActivePriceId] = useState<string | null>(null);
  const [docsPerMonth, setDocsPerMonth] = useState(5);

  // Per-doc costs
  const LAWYER_PER_DOC = 300;
  const LD_PER_DOC = 2.36;
  const docsPerYear = docsPerMonth * 12;
  const lawyerYear = docsPerYear * LAWYER_PER_DOC;
  const ldYear = docsPerYear * LD_PER_DOC;
  const savings = Math.round(lawyerYear - ldYear);

  // Best-deal logic: Pro = 25 docs/yr; if 13–48 docs/yr (≈1–4 docs/mo more nuanced),
  // Pro is the sweet spot. Use Pro when 2–4 docs/month.
  const bestPlanId = useMemo(() => {
    if (docsPerMonth <= 1) return "free";
    if (docsPerMonth <= 1.5) return "starter";
    if (docsPerMonth <= 8) return "pro";
    return "enterprise";
  }, [docsPerMonth]);

  // Pulse the savings number when slider changes
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    setPulse((p) => p + 1);
  }, [savings]);

  const handleBuy = async (priceId: string | null, planName: string) => {
    if (!priceId) {
      if (planName === "Enterprise") {
        navigate({ to: "/contact" });
        return;
      }
      navigate({ to: "/" });
      return;
    }
    if (!user) {
      toast.info("Please sign in to continue.");
      navigate({ to: "/auth", search: { redirect: "/pricing" } as never });
      return;
    }
    if (isPro && priceId.startsWith("credits_")) {
      toast.success("You already have an active credit pack.");
      return;
    }
    setActivePriceId(priceId);
    await openCheckout({
      priceId,
      userId: user.id,
      customerEmail: user.email ?? undefined,
      successUrl: `${window.location.origin}/account?checkout=success`,
    });
    setActivePriceId(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section className="border-b border-border py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h1 className="text-[36px] font-bold tracking-tight">
              Simple pricing. No subscriptions.
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-[#6B7280]">
              One-time payment. Credits valid for 12 months. No recurring charges.
            </p>
            <div className="mt-6 inline-block rounded-xl bg-[#EEF2FF] px-6 py-3 text-sm text-[#1B3A6B]">
              vs. <span className="font-semibold">$300/hr</span> for a lawyer consultation →{" "}
              <span className="font-semibold">as low as $2.36 per document</span>
            </div>
          </div>
        </section>

        {/* ROI Calculator */}
        <section className="border-b border-border bg-[#F8FAFC] py-14">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-block rounded-full bg-[#1B3A6B] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                ROI Calculator
              </div>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#111827]">
                See how much you save vs. a lawyer
              </h2>
              <p className="mt-2 text-sm text-[#6B7280]">
                Drag the slider to match your real workload.
              </p>
            </div>

            <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-card">
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="roi-slider" className="text-sm font-medium text-[#374151]">
                    Documents you sign per month
                  </label>
                  <span className="tabular-nums rounded-md bg-[#1B3A6B] px-2.5 py-1 text-sm font-bold text-white">
                    {docsPerMonth}
                  </span>
                </div>
                <input
                  id="roi-slider"
                  type="range"
                  min={1}
                  max={30}
                  step={1}
                  value={docsPerMonth}
                  onChange={(e) => setDocsPerMonth(Number(e.target.value))}
                  className="roi-slider mt-3 w-full"
                  style={{
                    background: `linear-gradient(to right, #15803D 0%, #15803D ${
                      ((docsPerMonth - 1) / 29) * 100
                    }%, #E5E7EB ${((docsPerMonth - 1) / 29) * 100}%, #E5E7EB 100%)`,
                  }}
                />
                <div className="mt-1 flex justify-between text-[11px] text-[#9CA3AF]">
                  <span>1</span>
                  <span>15</span>
                  <span>30</span>
                </div>
              </div>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-rose-700">
                    Lawyer review
                  </div>
                  <div className="mt-1 tabular-nums text-sm text-[#6B7280]">
                    ${LAWYER_PER_DOC.toLocaleString()}/contract
                  </div>
                  <div className="mt-2 tabular-nums text-2xl font-bold text-[#111827]">
                    ${lawyerYear.toLocaleString()}
                    <span className="text-sm font-medium text-[#6B7280]">/year</span>
                  </div>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                    legaldecoder
                  </div>
                  <div className="mt-1 tabular-nums text-sm text-[#6B7280]">
                    ${LD_PER_DOC.toFixed(2)}/contract
                  </div>
                  <div className="mt-2 tabular-nums text-2xl font-bold text-[#111827]">
                    ${ldYear.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    <span className="text-sm font-medium text-[#6B7280]">/year</span>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-col items-center justify-center rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 p-5 text-center ring-1 ring-emerald-300">
                <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                  You save
                </div>
                <div
                  key={pulse}
                  className="roi-pulse tabular-nums mt-1 text-4xl font-extrabold text-[#15803D]"
                >
                  ${savings.toLocaleString()}
                  <span className="ml-1 text-base font-semibold text-emerald-700">/year</span>
                </div>
              </div>
            </div>
          </div>
          <style>{`
            .roi-slider {
              -webkit-appearance: none;
              appearance: none;
              height: 6px;
              border-radius: 999px;
              outline: none;
              cursor: pointer;
            }
            .roi-slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 20px; height: 20px;
              border-radius: 999px;
              background: #fff;
              border: 3px solid #34D399;
              box-shadow: 0 4px 12px rgba(52,211,153,0.4);
              cursor: pointer;
              transition: transform .15s ease;
            }
            .roi-slider::-webkit-slider-thumb:hover { transform: scale(1.15); }
            .roi-slider::-moz-range-thumb {
              width: 20px; height: 20px;
              border-radius: 999px;
              background: #fff;
              border: 3px solid #34D399;
              box-shadow: 0 4px 12px rgba(52,211,153,0.4);
              cursor: pointer;
            }
            @keyframes roi-pulse {
              0%   { transform: scale(1);    text-shadow: 0 0 0 rgba(52,211,153,0); }
              40%  { transform: scale(1.08); text-shadow: 0 0 24px rgba(52,211,153,0.6); }
              100% { transform: scale(1);    text-shadow: 0 0 0 rgba(52,211,153,0); }
            }
            .roi-pulse { animation: roi-pulse .55s ease-out; display: inline-block; }
            @media (prefers-reduced-motion: reduce) {
              .roi-pulse { animation: none; }
            }
          `}</style>
        </section>

        {/* Pricing cards */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {plans.map((plan) => {
                const isLoading = loading && activePriceId === plan.priceId;
                const isBest = plan.id === bestPlanId;
                return (
                  <div
                    key={plan.id}
                    className={cn(
                      "relative flex flex-col rounded-2xl bg-card p-6 shadow-card transition-all",
                      plan.featured
                        ? "border-2 border-[#1B3A6B] shadow-elegant lg:-translate-y-2"
                        : "border border-border",
                      isBest && "ring-2 ring-emerald-400 ring-offset-2 ring-offset-background",
                    )}
                  >
                    {isBest && (
                      <div className="absolute -top-3 right-3 rounded-full bg-emerald-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-md">
                        Best for you
                      </div>
                    )}
                    {plan.badge && (
                      <div
                        className={cn(
                          "mb-4 inline-block self-start rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider",
                          plan.featured && "absolute -top-3 left-1/2 -translate-x-1/2",
                        )}
                        style={{ background: plan.badge.bg, color: plan.badge.color }}
                      >
                        {plan.badge.label}
                      </div>
                    )}

                    <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                    <p className="mt-1 min-h-[36px] text-[13px] italic text-[#6B7280]">
                      {plan.scenario}
                    </p>

                    <div className="mt-5 flex items-baseline gap-2">
                      <span className="text-[40px] font-bold leading-none tracking-tight">
                        {plan.price}
                      </span>
                      {plan.originalPrice && (
                        <span className="text-sm text-[#9CA3AF] line-through">
                          {plan.originalPrice}
                        </span>
                      )}
                    </div>
                    {plan.sub && (
                      <p
                        className={cn(
                          "mt-1 text-[13px]",
                          plan.sub.includes("Save") ? "text-[#15803D]" : "text-[#9CA3AF]",
                        )}
                      >
                        {plan.sub}
                      </p>
                    )}
                    <p className="mt-2 text-sm text-[#6B7280]">{plan.perDoc}</p>

                    <Button
                      variant={plan.ctaVariant === "primary" ? "hero" : "outline"}
                      size={plan.featured ? "lg" : "default"}
                      className="mt-6 w-full gap-2"
                      onClick={() => handleBuy(plan.priceId, plan.name)}
                      disabled={isLoading || (loading && activePriceId !== null)}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading…
                        </>
                      ) : (
                        <>
                          {plan.cta}
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </Button>

                    <ul className="mt-6 space-y-3">
                      {plan.features.map((f) => (
                        <li key={f} className="flex gap-2 text-sm text-foreground">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#15803D]" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Question packs */}
        <section className="border-t border-border bg-[#F9FAFB] py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-[20px] font-bold text-foreground">
                Need more follow-up questions?
              </h2>
              <p className="mx-auto mt-2 max-w-xl text-sm text-[#6B7280]">
                Add question packs anytime. Use across all your previously analyzed documents.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {questionPacks.map((pack) => {
                const isLoading = loading && activePriceId === pack.priceId;
                return (
                  <div
                    key={pack.id}
                    className="flex flex-col items-center rounded-xl border border-border bg-card p-5 text-center shadow-card"
                  >
                    <div className="text-base font-semibold text-foreground">{pack.label}</div>
                    <div className="mt-2 text-2xl font-bold">{pack.price}</div>
                    <div className="text-[13px] text-[#6B7280]">{pack.per}</div>
                    {pack.save && (
                      <div className="mt-1 text-[12px] font-medium text-[#15803D]">{pack.save}</div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4 w-full"
                      onClick={() => handleBuy(pack.priceId, pack.label)}
                      disabled={isLoading || (loading && activePriceId !== null)}
                    >
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Pack"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-[20px] font-bold text-foreground">
              Frequently Asked Questions
            </h2>
            <Accordion
              type="single"
              collapsible
              className="mt-8 rounded-2xl border border-border bg-card px-6 shadow-card"
            >
              {faqs.map((item) => (
                <AccordionItem key={item.q} value={item.q} className="last:border-b-0">
                  <AccordionTrigger className="py-5 text-left text-base font-semibold">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-[#6B7280]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <p className="mt-8 text-center text-xs text-[#9CA3AF]">
              NOT LEGAL ADVICE. FOR INFORMATIONAL PURPOSES ONLY.{" "}
              <Link to="/terms" className="underline">
                Terms
              </Link>
            </p>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}
